import React, { useState, useEffect } from 'react';
import {
  ShoppingCart,
  TrendingUp,
  DollarSign,
  Download,
  Package,
  Clock,
  Sun,
  Sunset,
  Moon
} from 'lucide-react';
import { useSiteContext } from '../contexts/SiteContext';

// Currency configuration
const CURRENCY = '£';

const OrderAnalytics = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('today');
  const [showOrdersList, setShowOrdersList] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    averageOrderValue: 0,
    conversionRate: 0
  });
  const [insights, setInsights] = useState({
    averageProductsPerOrder: 0,
    timeOfDayDistribution: {
      morning: 0,
      afternoon: 0,
      evening: 0,
      night: 0
    },
    peakTime: 'morning'
  });
  const [insightsLoading, setInsightsLoading] = useState(true);

  useEffect(() => {
    if (!selectedSite) return;
    fetchOrderData();
    fetchOrderInsights();
  }, [user, dateRange, selectedSite]);


  const fetchOrderData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        dateRange,
        siteId: selectedSite?.id || ''
      });
      const response = await fetch(`/app/api/analytics/orders.php?${params}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching order data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderInsights = async () => {
    setInsightsLoading(true);
    try {
      const params = new URLSearchParams({
        dateRange,
        siteId: selectedSite?.id || ''
      });
      const response = await fetch(`/app/api/analytics/order-insights.php?${params}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setInsights({
            averageProductsPerOrder: data.stats.averageProductsPerOrder,
            timeOfDayDistribution: data.timeOfDayDistribution,
            peakTime: data.peakTime
          });
          setStats({
            totalOrders: data.stats.totalOrders,
            totalRevenue: data.stats.totalRevenue,
            averageOrderValue: data.stats.averageOrderValue,
            conversionRate: 0
          });
        }
      }
    } catch (error) {
      console.error('Error fetching order insights:', error);
    } finally {
      setInsightsLoading(false);
    }
  };

  const exportData = async () => {
    try {
      const params = new URLSearchParams({
        dateRange,
        siteId: selectedSite?.id || ''
      });
      const response = await fetch(`/app/api/analytics/orders/export.php?${params}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `orders-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Order Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            {selectedSite ? `Analyzing orders for ${selectedSite.name}` : 'Track and analyze your Magento order data across all sites'}
          </p>
        </div>
        <button
          onClick={exportData}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Download className="h-4 w-4 mr-2" />
          Export Data
        </button>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center gap-4">
          <label htmlFor="dateRange" className="text-sm font-medium text-gray-700">
            Date Range:
          </label>
          <select
            id="dateRange"
            name="dateRange"
            className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Prev Month</option>
            <option value="this_year">This Year</option>
            <option value="last_year">Prev Year</option>
          </select>
        </div>
      </div>

      {/* Order Insights Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
            Order Insights
          </h3>

          {insightsLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Total Orders</span>
                  <ShoppingCart className="h-5 w-5 text-blue-600" />
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {stats.totalOrders.toLocaleString()}
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Total Revenue</span>
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {CURRENCY}{stats.totalRevenue.toLocaleString()}
                </div>
              </div>

              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Avg Order Value</span>
                  <TrendingUp className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {CURRENCY}{stats.averageOrderValue.toFixed(2)}
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Avg Products/Order</span>
                  <Package className="h-5 w-5 text-purple-600" />
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {insights.averageProductsPerOrder.toFixed(1)}
                </div>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Peak Order Time</span>
                  <Clock className="h-5 w-5 text-indigo-600" />
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {insights.peakTime.charAt(0).toUpperCase() + insights.peakTime.slice(1)}
                </div>
              </div>
            </div>
          )}

          {/* Time of Day Distribution */}
          {!insightsLoading && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Orders by Time of Day</h4>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Morning</span>
                    <Sun className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {insights.timeOfDayDistribution.morning}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">6am - 12pm</div>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Afternoon</span>
                    <Sun className="h-5 w-5 text-orange-600" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {insights.timeOfDayDistribution.afternoon}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">12pm - 6pm</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Evening</span>
                    <Sunset className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {insights.timeOfDayDistribution.evening}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">6pm - 12am</div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Night</span>
                    <Moon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {insights.timeOfDayDistribution.night}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">12am - 6am</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Orders Table */}
      {showOrdersList && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Orders in Selected Date Range
              </h3>
              <button
                onClick={() => setShowOrdersList(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Hide Orders
              </button>
            </div>

          {loading ? (
            <div className="animate-pulse space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : orders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Site
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{order.increment_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.customer_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.site_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.item_count || 0} items
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {CURRENCY}{order.grand_total}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          order.status === 'complete'
                            ? 'bg-green-100 text-green-800'
                            : order.status === 'processing'
                            ? 'bg-blue-100 text-blue-800'
                            : order.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No orders found</h3>
              <p className="mt-1 text-sm text-gray-500">
                No orders match your current filters or you haven't synced any order data yet.
              </p>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default OrderAnalytics;